// ─────────────────────────────────────────────────────────────────────────────
// useIntegrations — loads integration connection status and drives the Slack
// connect / disconnect / test actions.
//   GET    /settings/integrations            → status
//   PUT    /settings/integrations/slack      → connect
//   DELETE /settings/integrations/slack      → disconnect
//   POST   /settings/integrations/slack/test → send test message
//
// Unlike Company/Notifications, integrations use discrete inline actions (not the
// header Save Changes button), so this hook exposes the actions directly.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from "react";
import {
  getIntegrations, connectSlack, disconnectSlack, testSlack,
} from "../../../services/settingsService";

export function useIntegrations() {
  const [slackConnected, setSlackConnected] = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [busy,     setBusy]     = useState(false);         // connect/disconnect in flight
  const [testState, setTestState] = useState("idle");      // idle | sending | sent | failed

  const load = useCallback(async () => {
    try {
      const data = await getIntegrations();
      const slack = (data?.integrations ?? []).find((i) => i.type === "slack");
      setSlackConnected(!!slack?.connected);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
  }, [load]);

  const connect = useCallback(async (webhookUrl) => {
    setError(null);
    setBusy(true);
    try {
      const status = await connectSlack(webhookUrl);
      setSlackConnected(!!status?.connected);
      return true;
    } catch (err) {
      setError(err.message || "Failed to connect Slack.");
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      await disconnectSlack();
      setSlackConnected(false);
      setTestState("idle");
    } catch (err) {
      setError(err.message || "Failed to disconnect Slack.");
    } finally {
      setBusy(false);
    }
  }, []);

  const test = useCallback(async () => {
    setError(null);
    setTestState("sending");
    try {
      await testSlack();
      setTestState("sent");
      setTimeout(() => setTestState("idle"), 3000);
    } catch (err) {
      setTestState("failed");
      setError(err.message || "Slack test failed.");
      setTimeout(() => setTestState("idle"), 3000);
    }
  }, []);

  return { slackConnected, loading, error, busy, testState, connect, disconnect, test };
}
